import Link from 'next/link'
import { FC, useEffect, useState } from 'react'
import { Button } from '~/core/ui/Button'
import { PRIVACY_POLICY_URL } from '~/core/constants/url'

const COOKIE_CONSENT_KEY = 'cookie-consent'

export type CookieConsent = 'accepted' | 'declined' | null

interface CookiesBannerProps {
  onConsentChange?: (consent: CookieConsent) => void
}

const CookiesBanner: FC<CookiesBannerProps> = ({ onConsentChange }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsent
    if (!consent) {
      setIsVisible(true)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setIsVisible(false)
    onConsentChange?.('accepted')
  }

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
    setIsVisible(false)
    onConsentChange?.('declined')
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start sm:items-center gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
            {`We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking 'Accept', you consent to our use of cookies. Learn more in our `}
            <Link
              href={PRIVACY_POLICY_URL}
              className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 underline">
              Privacy Policy
            </Link>
            .
          </p>
          <div className="flex items-center gap-3 sm:gap-2 sm:w-auto shrink-0">
            <Button
              type="secondary"
              size="md"
              label="Decline"
              onClick={handleDecline}
              className="flex-1 sm:flex-none sm:min-w-[100px]"
            />
            <Button
              type="primary"
              size="md"
              label="Accept"
              onClick={handleAccept}
              className="flex-1 sm:flex-none sm:min-w-[100px]"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CookiesBanner
